import { Stack } from '@mui/material';
import { orderBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import AnchorWrapper from '@/components/AnchorWrapper';
import MultiPartScoreBadge from '@/components/MultiPartScoreBadge';
import VMPGenericChip from '@/components/VMPGenericChip';
import VMPVulnerabilityChip from '@/components/VMPVulnerabilityChip';
import { scorePartsNoTotal } from '@/pages/Assets';
import type { Asset } from '@/pages/Assets/Assets.types';
import { VMPE_CRITICALITY_PRIORITIES } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.constants';
import type {
  CriticalityTypes,
  VmpAssetType,
  VulnerabilitySeverityCountType
} from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';

export const importanceFormatter = (row: VmpAssetType) => {
  if (row?.criticality !== '') {
    return (
      <AnchorWrapper path="/asset" itemId={row.id}>
        <VMPGenericChip
          type="outlined"
          value={row.criticality as CriticalityTypes}
        />
      </AnchorWrapper>
    );
  }
  return null;
};

export const scoreFormatter = ({
  id,
  risk_score,
  risk_score_details
}: Asset) => {
  return (
    <AnchorWrapper path="/asset" itemId={id}>
      <MultiPartScoreBadge
        score={risk_score || 0}
        parts={scorePartsNoTotal(risk_score_details || {})}
      />
    </AnchorWrapper>
  );
};

export const vulnerabilitiesFormatter = (row: Asset) => {
  const sortedData = orderBy(
    Array.isArray(row.vulnerability_counts) ? row.vulnerability_counts : [],
    [
      (item: { severity: keyof typeof VMPE_CRITICALITY_PRIORITIES }) =>
        VMPE_CRITICALITY_PRIORITIES[item.severity]
    ],
    ['desc'] // descending for priority (Critical first)
  ) as VulnerabilitySeverityCountType[];

  return (
    <AnchorWrapper path="/asset" itemId={row.id}>
      <Stack spacing={1} direction="row">
        {sortedData?.map((vuln: VulnerabilitySeverityCountType) => (
          <div key={uuidv4()}>
            <VMPVulnerabilityChip vulnerability={vuln} />
          </div>
        ))}
      </Stack>
    </AnchorWrapper>
  );
};

export const assetsCellFormatter = (
  id?: string | number,
  text?: string | number | null
) => (
  <AnchorWrapper path="/asset" itemId={id}>
    {text || ''}
  </AnchorWrapper>
);

export const sortByPriorityAndCount = (
  a: VmpAssetType,
  b: VmpAssetType,
  ascending = true
) => {
  // Get the item_counts arrays from each widget
  const aCounts = a.vulnerability_counts || [];
  const bCounts = b.vulnerability_counts || [];

  // Find the highest priority in each widget
  const aHighestPriority = Math.min(
    ...aCounts.map(
      (item: VulnerabilitySeverityCountType) =>
        VMPE_CRITICALITY_PRIORITIES[
          item.severity as keyof typeof VMPE_CRITICALITY_PRIORITIES
        ] || 6
    )
  );
  const bHighestPriority = Math.min(
    ...bCounts.map(
      (item: VulnerabilitySeverityCountType) =>
        VMPE_CRITICALITY_PRIORITIES[
          item.severity as keyof typeof VMPE_CRITICALITY_PRIORITIES
        ] || 6
    )
  );

  // If priorities differ, sort by priority
  if (aHighestPriority !== bHighestPriority) {
    return ascending
      ? aHighestPriority - bHighestPriority
      : bHighestPriority - aHighestPriority;
  }

  // If priorities are the same, calculate total counts for that priority
  const aPriorityName = Object.keys(VMPE_CRITICALITY_PRIORITIES).find(
    (key) =>
      VMPE_CRITICALITY_PRIORITIES[
        key as keyof typeof VMPE_CRITICALITY_PRIORITIES
      ] === aHighestPriority
  );
  const bPriorityName = aPriorityName; // Same priority at this point

  const aTotalCount = aCounts
    .filter(
      (item: VulnerabilitySeverityCountType) => item.severity === aPriorityName
    )
    .reduce(
      (sum: number, item: VulnerabilitySeverityCountType) => sum + item.count,
      0
    );

  const bTotalCount = bCounts
    .filter(
      (item: VulnerabilitySeverityCountType) => item.severity === bPriorityName
    )
    .reduce(
      (sum: number, item: VulnerabilitySeverityCountType) => sum + item.count,
      0
    );

  // Sort by total count
  return ascending ? bTotalCount - aTotalCount : aTotalCount - bTotalCount;
};
